/**
 * SEO Utilities
 *
 * Pure utility functions for SEO-related operations like canonical URLs, hreflang validation and robots meta content construction..
 */

import {isFeatureEnabled} from '@/utilities/feature-enabled'
import {getAuthenticationRequirements} from '@/products/auth/util'
import type {
	GlobalConfigContentItem,
	AdvancedPageTemplateContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
/**
 * Generates a canonical URL for SEO purposes
 * @param host - The host domain
 * @param pageLocale - The page locale (e.g., 'en-US')
 * @param pagePath - The page path
 * @param pageCanonicalUrl - Optional explicit canonical URL
 * @returns The canonical URL
 */
export const getCanonicalUrl = (
	host: string,
	pageLocale: string,
	pagePath: string,
	pageCanonicalUrl: string
): string => {
	if (pageCanonicalUrl) return pageCanonicalUrl

	if (pagePath === '/') return `https://${host}/${pageLocale}`

	const pageUrlWithParams = `https://${host}/${pageLocale}${pagePath}`
	const pageUrl = pageUrlWithParams.split('?')[0] ?? ''

	if (pageUrlWithParams.includes('page=')) {
		const url = new URL(pageUrlWithParams)
		const searchParams = new URLSearchParams(url.search)
		const pageNumber = searchParams.get('page')

		if (pageNumber === '1') return pageUrl

		if (pageNumber) return `${pageUrl}?page=${pageNumber}`
	}

	return pageUrl
}

/**
 * Validates hreflang tags for XSS prevention and proper format
 * @param pageHrefLangs - The hreflang HTML string
 * @returns true if valid, false otherwise
 */
export const validHrefLangs = (pageHrefLangs: string): boolean => {
	// prevent XSS
	const BAD_WORDS = ['script', 'iframe']

	for (const word of BAD_WORDS) {
		if (pageHrefLangs.includes(word)) return false
	}

	// otherwise proceed
	const hrefLangs = pageHrefLangs.split('/>').slice(0, -1)
	for (const hrefLang of hrefLangs) {
		const hreflangWithoutWhiteSpaces = hrefLang.replace(/\s/g, '')
		// removed white spaces (since white spaces doesn't create an issue), and checking for a particular pattern of attributes that we want inside <link> tag
		if (hreflangWithoutWhiteSpaces) {
			if (
				hreflangWithoutWhiteSpaces.match(/</g)?.length !== 1 ||
				!/<linkrel=(?:"[^"]*"|'[^']*')href=(?:"[^"]*"|'[^']*')hreflang=(?:"[^"]*"|'[^']*')+/.test(
					hreflangWithoutWhiteSpaces
				)
			) {
				return false
			}
		}
	}
	return true
}

// Production environment - build comprehensive robots meta tag
export const buildRobotsContent = (
	page: Tersed<AdvancedPageTemplateContentItem>,
	globalConfig: Tersed<GlobalConfigContentItem>
): string => {
	const directives: string[] = []

	// Index/NoIndex - page level only
	// Logic: "yes" = noindex, "no" or empty = index (SEO-friendly default)
	const pageIndexValue = (
		page.elements.snippetPageSeoExternalNoindex as
			| {
					codename: string
			  }[]
			| undefined
	)?.[0]?.codename

	// Noindex auth-gated pages when the feature flag is on; per-page
	// External Noindex = "no" still wins as an explicit opt-in (GWP-5938)
	const gateLevel = getAuthenticationRequirements(page, globalConfig)
	const isGated = gateLevel === 'Authenticated' || gateLevel === 'Validated'
	if (
		isGated &&
		pageIndexValue !== 'no' &&
		isFeatureEnabled('noindexGatedPages', globalConfig)
	) {
		return 'noindex,nofollow'
	}

	const indexDirective: string =
		pageIndexValue === 'yes' ? 'noindex' : 'index'

	// Follow/NoFollow - page level only
	// Logic: "yes" = nofollow, "no" or empty = follow (SEO-friendly default)
	const pageFollowValue = (
		page.elements.snippetPageSeoPageNofollow as
			| {codename: string}[]
			| undefined
	)?.[0]?.codename
	const followDirective: string =
		pageFollowValue === 'yes' ? 'nofollow' : 'follow'

	directives.push(indexDirective, followDirective)

	// Max Image Preview - page level only with hardcoded default
	const pageMaxImagePreview = (
		page.elements.snippetPageSeoMetaRobotsMaxImagePreview as
			| {
					codename: string
			  }[]
			| undefined
	)?.[0]?.codename
	const maxImagePreview = pageMaxImagePreview || 'large' // Default to 'large' for GEO
	if (maxImagePreview && maxImagePreview !== 'none') {
		directives.push(`max-image-preview:${maxImagePreview}`)
	}

	// Max Snippet - page level only with hardcoded default
	const pageMaxSnippet = page.elements.snippetPageSeoMetaRobotsMaxSnippet
	const maxSnippet = pageMaxSnippet ?? -1 // Default to -1 (unlimited) for GEO
	directives.push(`max-snippet:${maxSnippet}`)

	// Max Video Preview - page level only with hardcoded default
	const pageMaxVideoPreview =
		page.elements.snippetPageSeoMetaRobotsMaxVideoPreview
	const maxVideoPreview = pageMaxVideoPreview ?? -1 // Default to -1 (unlimited) for GEO
	directives.push(`max-video-preview:${maxVideoPreview}`)

	// Additional directives
	if (
		(
			page.elements.snippetPageSeoMetaRobotsNoarchive as
				| {
						codename: string
				  }[]
				| undefined
		)?.[0]?.codename === 'yes'
	) {
		directives.push('noarchive')
	}
	if (
		(
			page.elements.snippetPageSeoMetaRobotsNosnippet as
				| {
						codename: string
				  }[]
				| undefined
		)?.[0]?.codename === 'yes'
	) {
		directives.push('nosnippet')
	}
	if (
		(
			page.elements.snippetPageSeoMetaRobotsNotranslate as
				| {
						codename: string
				  }[]
				| undefined
		)?.[0]?.codename === 'yes'
	) {
		directives.push('notranslate')
	}
	if (
		(
			page.elements.snippetPageSeoMetaRobotsNoimageindex as
				| {
						codename: string
				  }[]
				| undefined
		)?.[0]?.codename === 'yes'
	) {
		directives.push('noimageindex')
	}

	return directives.join(',')
}
